/**
 * Temporary table components used while Import Hub is being migrated to AG Grid.
 * TODO: Remove them after all tables in this microfrontend have moved to AG Grid.
 */
import { useTranslation } from '@mint-lib/i18n';
import {
  ChevronLeft,
  ChevronRight,
  PageFirst,
  PageLast,
} from '@mint-lib/icons';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import type { PaginatedDataResponse } from '../../api/types.js';
import { ROWS_PER_PAGE_OPTIONS } from '../../constants/tablesConfig.js';
import {
  useImportHubNavigate,
  useImportHubPaginatedSearch,
} from '../../hooks/useImportHubRouteApi.js';
import { initialSearchParams } from '../../queries/index.js';

type TablePaginationProps = {
  data: PaginatedDataResponse<unknown>;
};

export const TablePagination = ({ data }: TablePaginationProps) => {
  const { t } = useTranslation('@myn/import-hub');
  const searchParams = useImportHubPaginatedSearch();
  const navigate = useImportHubNavigate();

  const pageNumber = searchParams.pageNumber ?? initialSearchParams.pageNumber;
  const itemsPerPage =
    searchParams.itemsPerPage ?? initialSearchParams.itemsPerPage;
  const lastPageNumber =
    data.totalItems === 0 ? 1 : Math.ceil(data.totalItems / itemsPerPage);
  const fromItem =
    data.totalItems === 0 ? 0 : (pageNumber - 1) * itemsPerPage + 1;
  const toItem = Math.min(pageNumber * itemsPerPage, data.totalItems);
  const isVisible = data.totalItems > itemsPerPage;

  const navigateToPage = (nextPageNumber: number) => {
    navigate({
      search: (prevState) => ({
        ...prevState,
        pageNumber: nextPageNumber,
      }),
    } as Parameters<typeof navigate>[0]);
  };

  const handleItemsPerPageChange = (value: string) => {
    navigate({
      search: (prevState) => ({
        ...prevState,
        pageNumber: initialSearchParams.pageNumber,
        itemsPerPage: Number(value),
      }),
    } as Parameters<typeof navigate>[0]);
  };

  return (
    <div
      className={cn(
        'mr-[68px] flex h-12 items-center justify-end gap-4',
        typographyVariants({ variant: 'bodyShort01', color: 'primary' }),
        isVisible ? 'visible' : 'invisible',
      )}
    >
      <div className="flex items-center gap-2">
        <span>{t('Items per page:')}</span>
        <Select
          value={String(itemsPerPage)}
          onValueChange={handleItemsPerPageChange}
        >
          <SelectTrigger size="small" className="w-20 bg-ui-primary">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROWS_PER_PAGE_OPTIONS.map((option) => (
              <SelectItem key={option} value={String(option)}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <span>
        {fromItem}-{toItem} {t('of')} {data.totalItems}
      </span>
      <div className="flex items-center gap-1">
        <Button
          icon
          variant="text"
          size="small"
          onClick={() => navigateToPage(1)}
          disabled={pageNumber <= 1}
          aria-label={t('First page')}
        >
          <PageFirst />
        </Button>
        <Button
          icon
          variant="text"
          size="small"
          onClick={() => navigateToPage(pageNumber - 1)}
          disabled={pageNumber <= 1}
          aria-label={t('Previous page')}
        >
          <ChevronLeft />
        </Button>
        <Button
          icon
          variant="text"
          size="small"
          onClick={() => navigateToPage(pageNumber + 1)}
          disabled={pageNumber >= lastPageNumber}
          aria-label={t('Next page')}
        >
          <ChevronRight />
        </Button>
        <Button
          icon
          variant="text"
          size="small"
          onClick={() => navigateToPage(lastPageNumber)}
          disabled={pageNumber >= lastPageNumber}
          aria-label={t('Last page')}
        >
          <PageLast />
        </Button>
      </div>
    </div>
  );
};
